import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";

import CheckBox from "../../Components/Form Fields/checkbox";

import { useStakeDebit } from "../../CustomHooks/Queries";
import { useAcknowledgeMigration } from "../../CustomHooks/Mutations";

import { dispatchAPIErrorToast, shouldShowMigrationAcknowledgementFromStakeDebitResponse } from "../../Utils/helpers";
import { APIQueryKeys } from "../../Utils/constants";

import "./bank-migration-acknowledgement.scss";
import "./bank-migration-acknowledgement-responsive.scss";

function WarningTriangleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M8 1.5L15 14H1L8 1.5Z" stroke="#C4B73B" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M8 6.5v3.5" stroke="#C4B73B" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="8" cy="11.8" r="0.8" fill="#C4B73B" />
    </svg>
  );
}

export default function BankMigrationAcknowledgement() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [hasConfirmedPortalUpdate, setHasConfirmedPortalUpdate] = useState(false);

  const { data: stakeDebit, isLoading: isLoadingStakeDebit } = useStakeDebit();

  const showAcknowledgement = shouldShowMigrationAcknowledgementFromStakeDebitResponse(stakeDebit);

  const { isLoading: isAcknowledging, mutate: AcknowledgeMigration } = useAcknowledgeMigration({
    onSuccess: async () => {
      await queryClient.refetchQueries(APIQueryKeys.stakeDebit);
      navigate("/bank-migration-status", { replace: true, state: { fromMigrationAcknowledgement: true } });
    },
    onError: (err) =>
      dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "acknowledgeMigration" }),
  });

  useEffect(() => {
    if (isLoadingStakeDebit || showAcknowledgement) {
      return;
    }

    navigate("/home", { replace: true });
  }, [isLoadingStakeDebit, navigate, showAcknowledgement]);

  if (isLoadingStakeDebit) {
    return (
      <section className="authenticated-page bank-migration-acknowledgement">
        <div className="bank-migration-acknowledgement max-width-container">
          <CircularProgress />
        </div>
      </section>
    );
  }

  return (
    <section className="authenticated-page bank-migration-acknowledgement">
      <div className="bank-migration-acknowledgement max-width-container">
        <div className="bank-migration-acknowledgement__card">
          <div className="bank-migration-acknowledgement__content">
            <h1 className="bank-migration-acknowledgement__title">Confirm your payment portal is updated</h1>
            <p className="bank-migration-acknowledgement__description">
              Your old Stake Checking account is now closed. Before you finish, confirm you&apos;ve updated your
              property&apos;s payment portal with your new Stake account and routing numbers.
            </p>

            <div className="bank-migration-acknowledgement__warning" role="note">
              <span className="bank-migration-acknowledgement__warning-icon">
                <WarningTriangleIcon />
              </span>
              <div className="bank-migration-acknowledgement__warning-copy">
                <p className="bank-migration-acknowledgement__warning-title">Required step.</p>
                <p className="bank-migration-acknowledgement__warning-text">
                  Your old account details no longer work. If your property tries to pull rent from them, you may be
                  charged late fees.
                </p>
              </div>
            </div>

            <div className="bank-migration-acknowledgement__acknowledgement">
              <CheckBox
                checked={hasConfirmedPortalUpdate}
                onChange={(_event, checked) => setHasConfirmedPortalUpdate(checked)}
                ariaLabelledBy="bank-migration-acknowledgement-label"
                className="bank-migration-acknowledgement__checkbox"
              />
              <p
                className="bank-migration-acknowledgement__acknowledgement-text"
                id="bank-migration-acknowledgement-label"
                onClick={() => setHasConfirmedPortalUpdate((checked) => !checked)}
              >
                I understand that my old Stake Checking account is now closed and I have updated my property&apos;s
                payment portal with my new Stake account and routing number. I understand that this is a required step,
                and if I do not do so, it may result in late fees from the property because my old account information
                is no longer valid.
              </p>
            </div>
          </div>

          <div className="bank-migration-acknowledgement__actions">
            <button
              type="button"
              className="bank-migration-acknowledgement__confirm-btn"
              onClick={() => AcknowledgeMigration()}
              disabled={!hasConfirmedPortalUpdate || isAcknowledging}
            >
              {isAcknowledging ? (
                <CircularProgress size={20} sx={{ color: "#171B26" }} />
              ) : (
                "Confirm & finish migration"
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
